import { useCallback } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useStripeIntentAuthentication } from "@bw/shared/hooks";

export function RegistrationCardSetupChallenge({
  clientSecret,
  onAuthenticated,
  onProcessingChange,
}: {
  clientSecret: string;
  onAuthenticated: (authenticatedClientSecret: string) => Promise<void>;
  onProcessingChange: (processing: boolean) => void;
}) {
  const stripe = useStripe();
  const confirmIntent = useCallback(async (): Promise<boolean> => {
    if (!stripe) return false;
    // The SetupIntent already has the chosen card attached server-side, so
    // Stripe drives any 3-D Secure step from the client secret alone.
    const result = await stripe.confirmCardSetup(clientSecret);
    return !result.error && result.setupIntent?.status === "succeeded";
  }, [clientSecret, stripe]);
  const { phase, busy, authenticate } = useStripeIntentAuthentication({
    clientSecret,
    confirmIntent,
    onAuthenticated,
    onProcessingChange,
  });
  const isAuthenticationFailure = phase === "authentication_failed";
  const isReconciliationFailure = phase === "reconciliation_failed";
  return (
    <Stack spacing={1.5} role="status">
      {isAuthenticationFailure ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t verify your card with your bank. Please try again.
        </Alert>
      ) : isReconciliationFailure ? (
        <Alert severity="error" role="alert">
          Your card was verified, but we couldn&apos;t finish registration. Try
          again.
        </Alert>
      ) : phase === "reconciling" ? (
        <Typography variant="body2">
          Your card was verified. Finishing registration.
        </Typography>
      ) : phase === "complete" ? (
        <Typography variant="body2">Card verified.</Typography>
      ) : (
        <Typography variant="body2">
          Your bank needs to verify this card before we can hold your spot.
        </Typography>
      )}
      <Box>
        <Button
          type="button"
          variant="contained"
          size="small"
          onClick={() => void authenticate()}
          disabled={!stripe || busy || phase === "complete"}
          aria-busy={busy || undefined}
        >
          {phase === "confirming"
            ? "Verifying…"
            : phase === "reconciling"
              ? "Finishing…"
              : isAuthenticationFailure
                ? "Try again"
                : isReconciliationFailure
                  ? "Finish registration"
                  : phase === "complete"
                    ? "Verified"
                    : "Verify with your bank"}
        </Button>
      </Box>
    </Stack>
  );
}
